import { useEffect } from 'react';

/**
 * Custom hook to dynamically load external CSS and JavaScript resources
 * @param {Array<string>} cssUrls - Array of CSS file URLs to load
 * @param {Array<string>} jsUrls - Array of JavaScript file URLs to load (loaded sequentially in order)
 * @param {boolean} shouldLoad - Condition to determine if resources should be loaded
 */
const useExternalResources = (cssUrls = [], jsUrls = [], shouldLoad = true) => {
  useEffect(() => {
    if (!shouldLoad) return;

    const loadedElements = [];

    // Load CSS files (can be parallel)
    cssUrls.forEach((url) => {
      // Check if CSS is already loaded
      const existingLink = document.querySelector(`link[href="${url}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.type = 'text/css';
        document.head.appendChild(link);
        loadedElements.push(link);
      }
    });

    // Load JavaScript files sequentially to maintain dependency order
    const loadScriptsSequentially = async () => {
      for (const url of jsUrls) {
        // Check if JS is already loaded
        const existingScript = document.querySelector(`script[src="${url}"]`);
        if (!existingScript) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = false; // Load in order, not async
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
            loadedElements.push(script);
          });
        }
      }
    };

    loadScriptsSequentially().catch(error => {
      console.error('Error loading external scripts:', error);
    });

    // Cleanup function - optionally remove resources when component unmounts
    // Note: We're keeping resources loaded to avoid re-downloading on component remount
    return () => {
      // Uncomment below if you want to remove resources on unmount
      // loadedElements.forEach((element) => {
      //   if (element.parentNode) {
      //     element.parentNode.removeChild(element);
      //   }
      // });
    };
  }, [cssUrls, jsUrls, shouldLoad]);
};

export default useExternalResources;
